import {Component, ChangeDetectionStrategy} from '@angular/core';
import {Dialog} from '../../../../../interfaces/dialog/dialog';
import {DialogParams} from '../../../../../interfaces/dialog/dialog-params';
import {ClipboardService} from '../../../../../services/clipboard/clipboard.service';
import {Shortcode} from '../../../../../models/shortcode';
import { DatePipe } from '@angular/common';
import {getNextSign, getPreviousSign, getZodiacSign} from '../../../../../constants/zodiac';
import {FormsModule} from '@angular/forms';

@Component({
  imports: [
    FormsModule
],
  providers: [DatePipe],
  templateUrl: './translation-knowledge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './translation-knowledge.component.scss'
})
export class TranslationKnowledgeComponent implements Dialog {

  tab: 'simple' | 'dynamic' = 'simple';
  shortCodes: Shortcode[] = [];
  dynamicShortCodes: any = {
    Countdown: 300,
    Countdown_Day: 4,
    Countdown_From: '2025-06-13',
    Day_Month: -7,
    Day_Month_Year: -3,
    Day_Ago: 6,
    Week_Ago: 2,
    Month_Ago: 3
  };

  constructor(protected clipboard: ClipboardService, private datePipe: DatePipe) {
  }

  onDialogInit(params: DialogParams): void {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const zodiac = getZodiacSign(new Date());

    this.shortCodes = [
      new Shortcode('last_Year', this.datePipe.transform(new Date(year - 1, 0, 1), 'yyyy')!),
      new Shortcode('This_Year', this.datePipe.transform(new Date(year, 0, 1), 'yyyy')!),
      new Shortcode('Next_Year', this.datePipe.transform(new Date(year + 1, 0, 1), 'yyyy')!),
      new Shortcode('Last_Month', this.datePipe.transform(new Date(year, month - 1, 1), 'LLLL')!.toLowerCase()),
      new Shortcode('This_Month', this.datePipe.transform(new Date(year, month, 1), 'LLLL')!.toLowerCase()),
      new Shortcode('Next_Month', this.datePipe.transform(new Date(year, month + 1, 1), 'LLLL')!.toLowerCase()),
      new Shortcode('Last_Day', this.datePipe.transform(new Date(year, month, now.getDate() - 1), 'EEEE')!.toLowerCase()),
      new Shortcode('This_Day', this.datePipe.transform(now, 'EEEE')!.toLowerCase()),
      new Shortcode('Next_Day', this.datePipe.transform(new Date(year, month, now.getDate() + 1), 'EEEE')!.toLowerCase()),
      new Shortcode('Last_Zodiac', getPreviousSign(zodiac)),
      new Shortcode('This_Zodiac', zodiac),
      new Shortcode('Next_Zodiac', getNextSign(zodiac)),
      new Shortcode('Country', 'France (depends on IP)'),
    ];
  }

  getDynamicCode(code: string) {
    return '%' + code + ':' + this.dynamicShortCodes[code] + '%';
  }

  getCode(code: string): string {
    return '{' + code + '}';
  }

  countdown(): string {
    let seconds;
    if (this.dynamicShortCodes?.Countdown === 'midnight') {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      seconds = Math.floor((midnight.getTime() - now.getTime()) / 1000);
    } else {
      seconds = this.dynamicShortCodes?.Countdown ?? 0;
    }

    if (seconds >= 3600) {
      const hoursPart = Math.floor(seconds / 3600);
      const minutesPart = Math.floor((seconds % 3600) / 60);
      const secondsPart = seconds % 60;

      const hoursStr = hoursPart.toString().padStart(2, '0');
      const minutesStr = minutesPart.toString().padStart(2, '0');
      const secondsStr = secondsPart.toString().padStart(2, '0');

      return `${hoursStr}:${minutesStr}:${secondsStr}`;
    } else {
      const minutesPart = Math.floor(seconds / 60);
      const secondsPart = seconds % 60;

      const minutesStr = minutesPart.toString().padStart(2, '0');
      const secondsStr = secondsPart.toString().padStart(2, '0');

      return `${minutesStr}:${secondsStr}`;
    }
  }

  countdownDay(): string {
    const totalDays = this.dynamicShortCodes?.Countdown_Day ?? 0;
    const totalSeconds = Math.floor(totalDays * 24 * 60 * 60);

    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`;
  }

  countdownFrom(): string {
    const dateString = this.dynamicShortCodes?.Countdown_From;
    if (!dateString) return '';

    const targetDate = new Date(dateString);
    const now = new Date();

    const diffMs = targetDate.getTime() - now.getTime();
    if (diffMs <= 0) return '0 days 0 hours 0 minutes 0 seconds';

    const totalSeconds = Math.floor(diffMs / 1000);

    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`;
  }

  dayMonth(): string {
    const offset = this.dynamicShortCodes?.Day_Month ?? 0;
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + offset);

    const day = baseDate.getDate();
    const monthName = baseDate.toLocaleString('en-US', { month: 'long' });

    return `${day} ${monthName}`;
  }

  dayMonthYear(): string {
    const offset = this.dynamicShortCodes?.Day_Month_Year ?? 0;
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + offset);

    const day = baseDate.getDate();
    const month = baseDate.toLocaleString('en-US', { month: 'long' });
    const year = baseDate.getFullYear();

    return `${day} ${month} ${year}`;
  }
}
